import { type MessageDescriptor } from "@lingui/core";
import { observer } from "mobx-react-lite";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { PermissionBit } from "../api";
import { hasMonitoringAccess, hasPermission } from "../constants/permissions";
import { assert } from "../utils/utils";
import { AppModel } from "./app-model";
import { pageTitles } from "./page-titles";

/** One table under `/patient/:id/raw`. */
interface RawDataSection {
  segment: string;
  title: MessageDescriptor;
  /** Omitted when the section is visible to anyone who can reach `/raw`. */
  show?: (permissions: PermissionBit[]) => boolean;
}

/**
 * The `/raw` tables in nav order, with the visibility rule each one carries.
 * Shared by the sidebar and the `/raw` index redirect so the landing page is
 * always the first entry the user actually sees in the menu — LVFP for most,
 * but Vitals for someone without the LVFP, DRI and monitoring bits.
 */
export const RAW_DATA_SECTIONS: RawDataSection[] = [
  {
    segment: "lvfp",
    title: pageTitles.lvfp,
    show: (permissions) =>
      hasPermission(permissions, PermissionBit.ACCESS_LVFP),
  },
  {
    segment: "dri",
    title: pageTitles.dri,
    show: (permissions) => hasPermission(permissions, PermissionBit.ACCESS_DRI),
  },
  {
    segment: "afib",
    title: pageTitles.afib,
    show: hasMonitoringAccess,
  },
  {
    segment: "vitals",
    title: pageTitles.vitals,
  },
  {
    segment: "symptoms",
    title: pageTitles.symptoms,
  },
  {
    segment: "ppg",
    title: pageTitles.ppgData,
  },
  {
    segment: "ecg",
    title: pageTitles.ecgData,
  },
];

/**
 * Landing page for `/patient/:id/raw`, which the nav parent's label links to.
 * Resolves the first section the user may see instead of a fixed one, so the
 * page you land on is the one at the top of your own menu.
 */
export const RawIndexRedirect = observer(function RawIndexRedirect({
  app,
}: {
  app: AppModel;
}) {
  const permissions = app.seerlinqApi.permissions;
  const section = RAW_DATA_SECTIONS.find(
    (candidate) => candidate.show?.(permissions) ?? true,
  );
  // Vitals carries no bit, so the list can never resolve to nothing.
  assert(section !== undefined, "No visible raw data section");

  return <Navigate to={section.segment} replace />;
});

/**
 * Segments that used to sit flat under `/patient/:id/` and now live under
 * `/patient/:id/raw/`. Kept as redirect aliases so bookmarked links keep
 * working; removal is a follow-up, two releases out. Frozen to the historical
 * set rather than derived from `RAW_DATA_SECTIONS` — a section added later
 * never had a flat URL to redirect from.
 */
export const RAW_ALIAS_SEGMENTS = [
  "lvfp",
  "dri",
  "afib",
  "vitals",
  "symptoms",
  "ppg",
  "ecg",
] as const;

/**
 * Forwards an old flat URL to its `/raw` home. Only the patient prefix is
 * rewritten, so nested paths (`ppg/add`) survive, and `search`/`hash` are
 * carried through verbatim — `location.search` is the raw string, so repeated
 * params (`?uuid=a&uuid=b`, which the PPG table reads via `getAll`) are
 * preserved where a bare `<Navigate to=".../raw/ppg">` would drop them.
 * `replace` keeps the alias out of the history stack so Back doesn't bounce.
 */
export function RawAliasRedirect() {
  const { id } = useParams<{ id: string }>();
  const { pathname, search, hash } = useLocation();
  assert(id !== undefined, "Patient ID param not found");
  const prefix = `/patient/${id}`;

  return (
    <Navigate
      replace
      to={{
        pathname: `${prefix}/raw${pathname.slice(prefix.length)}`,
        search,
        hash,
      }}
    />
  );
}
